<template>
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="900"
        scrollable>
        <v-card>
            <v-card-title class="d-flex align-center">
                Seleziona la macchina
                <v-spacer />
                <v-btn icon variant="text" size="small" @click="chiudi">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-row dense class="mb-2">
                    <v-col cols="12" sm="4">
                        <v-text-field v-model="ricerca" label="Cerca macchina" prepend-inner-icon="mdi-magnify"
                            density="compact" variant="outlined" hide-details clearable />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="macroAreaId" :items="macroAree" item-title="nome" item-value="id"
                            label="Tutte le macro aree" density="compact" variant="outlined" hide-details clearable
                            @update:model-value="onCambioMacroArea" />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="repartoId" :items="repartiFiltrati" item-title="nome" item-value="id"
                            label="Tutti i reparti" density="compact" variant="outlined" hide-details clearable />
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-select v-model="stato" :items="statiDisponibili" item-title="label" item-value="value"
                            label="Tutti gli stati" density="compact" variant="outlined" hide-details clearable />
                    </v-col>
                </v-row>

                <div class="text-caption text-medium-emphasis mb-2">
                    {{ macchine.length }} macchin{{ macchine.length === 1 ? 'a trovata' : 'e trovate' }}
                </div>

                <div style="max-height: 480px; overflow-y: auto">
                    <div v-if="!caricamento && macchine.length === 0" class="text-center text-medium-emphasis pa-8">
                        Nessuna macchina trovata con questi filtri.
                    </div>

                    <v-row dense>
                        <v-col v-for="m in macchine" :key="m.id" cols="12" md="6">
                            <v-card variant="outlined" class="rounded-lg pa-3 macchina-card"
                                :class="{ 'macchina-card--selezionata': selezionataId === m.id }"
                                @click="selezionataId = m.id">
                                <div class="d-flex align-center justify-space-between">
                                    <div class="font-weight-medium">{{ m.nome }}</div>
                                    <v-chip size="x-small" :color="coloreStato(m.stato)" variant="flat">
                                        {{ etichettaStato(m.stato) }}
                                    </v-chip>
                                </div>
                                <div class="text-caption text-medium-emphasis">
                                    {{ m.macroAreaNome }}<template v-if="m.repartoNome"> · {{ m.repartoNome }}</template>
                                </div>
                                <div class="text-caption mt-1">
                                    <span class="text-medium-emphasis">Ultima:</span>
                                    {{ formatta(m.ultimaManutenzioneData) }}
                                    <template v-if="m.prossimaManutenzioneData">
                                        <span class="text-medium-emphasis ml-2">Prossima:</span>
                                        <span class="text-primary">{{ formatta(m.prossimaManutenzioneData) }}</span>
                                    </template>
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">Annulla</v-btn>
                <v-btn color="primary" :disabled="!selezionataId" @click="conferma">Continua</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { STATI_MACCHINA, coloreStato, etichettaStato } from "@/constants/statoMacchina.js";

export default {
    name: "SelezionaMacchinaDialog",

    props: {
        modelValue: { type: Boolean, default: false },
    },
    emits: ["update:modelValue", "seleziona"],

    data() {
        return {
            ricerca: "",
            macroAreaId: null,
            repartoId: null,
            stato: null,
            selezionataId: null,
            caricamento: false,
            debounce: null,
            statiDisponibili: STATI_MACCHINA,
        };
    },

    computed: {
        macchine() {
            return this.$store.getters["macchine/lista"];
        },
        macroAree() {
            return this.$store.getters["configurazione/macroAree"];
        },
        repartiFiltrati() {
            const reparti = this.$store.getters["configurazione/reparti"];
            if (!this.macroAreaId) return reparti;
            return reparti.filter(r => r.macroAreaId === this.macroAreaId);
        },
    },

    watch: {
        modelValue(val) {
            if (val) {
                this.ricerca = "";
                this.macroAreaId = null;
                this.repartoId = null;
                this.stato = null;
                this.selezionataId = null;
                this.$store.dispatch("configurazione/caricaMacroAree");
                this.$store.dispatch("configurazione/caricaReparti");
                this.carica();
            }
        },
        ricerca() {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => this.carica(), 300);
        },
        macroAreaId() {
            this.carica();
        },
        repartoId() {
            this.carica();
        },
        stato() {
            this.carica();
        },
    },

    methods: {
        async carica() {
            this.caricamento = true;
            try {
                await this.$store.dispatch("macchine/carica", {
                    search: this.ricerca || undefined,
                    macroAreaId: this.macroAreaId || undefined,
                    repartoId: this.repartoId || undefined,
                    stato: this.stato ?? undefined,
                });
            } catch (error) {
                console.log(error);
            }
            this.caricamento = false;
        },

        onCambioMacroArea() {
            this.repartoId = null;
        },

        coloreStato,
        etichettaStato,

        formatta(data) {
            if (!data) return "—";
            return new Date(data).toLocaleDateString("it-IT");
        },

        chiudi() {
            this.$emit("update:modelValue", false);
        },

        conferma() {
            if (!this.selezionataId) return;
            this.$emit("seleziona", this.selezionataId);
            this.chiudi();
        },
    },
};
</script>

<style scoped>
.macchina-card {
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
}

.macchina-card:hover {
    border-color: rgb(var(--v-theme-primary));
}

.macchina-card--selezionata {
    border-color: rgb(var(--v-theme-primary));
    border-width: 2px;
    background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
